import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, MapPin, Clock, Youtube, Instagram, Navigation } from 'lucide-react';
import { businessInfo } from '../data/menuData';

// WhatsApp icon component
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const whatsappLink = `https://wa.me/${businessInfo.whatsapp}?text=Hello%20MKD%20Bakery!%20I%20would%20like%20to%20place%20an%20order.`;

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: businessInfo.phone,
      link: `tel:${businessInfo.phone}`,
      action: 'Call Now'
    },
    {
      icon: MapPin,
      title: 'Address',
      value: businessInfo.address,
      link: 'https://www.google.com/maps/search/?api=1&query=MKD+Bakery+Tegeta+Nyaishozi+Dar+es+Salaam',
      action: 'Get Directions'
    },
    {
      icon: Clock,
      title: 'Opening Hours',
      value: businessInfo.hours,
      link: null,
      action: null
    }
  ];

  return (
    <section id="contact" className="py-20 md:py-32 bg-white dark:bg-gray-800" data-testid="contact-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="font-accent text-2xl text-bakery-primary">Get in Touch</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-bakery-text dark:text-bakery-bg mt-2">
            Contact & <span className="text-bakery-primary">Location</span>
          </h2>
          <p className="text-bakery-text/70 dark:text-bakery-bg/70 mt-4 max-w-2xl mx-auto">
            Visit us at our bakery in Tegeta or give us a call to place your order
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {contactInfo.map((info, index) => (
              <div
                key={info.title}
                className="flex items-start gap-4 p-6 bg-bakery-bg dark:bg-gray-900 rounded-2xl"
                data-testid={`contact-info-${info.title.toLowerCase()}`}
              >
                <div className="p-4 bg-bakery-primary/10 dark:bg-bakery-primary/20 rounded-xl">
                  <info.icon size={28} className="text-bakery-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-bakery-text dark:text-bakery-bg text-lg">{info.title}</h3>
                  <p className="text-bakery-text/70 dark:text-bakery-bg/70 mt-1">{info.value}</p>
                  {info.link && (
                    <a
                      href={info.link}
                      target={info.title === 'Address' ? '_blank' : undefined}
                      rel={info.title === 'Address' ? 'noopener noreferrer' : undefined}
                      className="inline-flex items-center gap-2 mt-3 text-bakery-primary font-medium hover:underline"
                    >
                      {info.action}
                      <Navigation size={16} />
                    </a>
                  )}
                </div>
              </div>
            ))}

            {/* Order Methods */}
            <div className="p-6 bg-bakery-text dark:bg-gray-900 rounded-2xl text-bakery-bg">
              <h3 className="font-heading text-2xl font-bold mb-4">How to Order</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-bakery-primary flex items-center justify-center font-bold">
                    1
                  </div>
                  <p>Call us directly at <span className="font-semibold">{businessInfo.phone}</span></p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center font-bold">
                    2
                  </div>
                  <p>Message us on <span className="font-semibold text-[#25D366]">WhatsApp</span></p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-bakery-primary flex items-center justify-center font-bold">
                    3
                  </div>
                  <p>Order via <span className="font-semibold text-bakery-primary">Msosi App</span> for delivery</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-bakery-primary flex items-center justify-center font-bold">
                    4
                  </div>
                  <p>Visit our bakery in Tegeta for dine-in or takeaway</p>
                </div>
              </div>
              
              {/* WhatsApp Order Button */}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-4 rounded-full font-medium hover:bg-[#20BD5A] transition-colors duration-300 mt-6 w-full"
                data-testid="contact-whatsapp-btn"
              >
                <WhatsAppIcon size={22} />
                Order via WhatsApp
              </a>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href={businessInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white rounded-full font-medium hover:opacity-90 transition-opacity"
                data-testid="instagram-link"
              >
                <Instagram size={20} />
                Instagram
              </a>
              <a
                href={businessInfo.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-full font-medium hover:bg-red-700 transition-colors duration-300"
                data-testid="youtube-link"
              >
                <Youtube size={20} />
                YouTube
              </a>
            </div>
          </motion.div>

          {/* Map - MKD Bakery search embed */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="map-container h-full min-h-[400px] lg:min-h-[500px] rounded-2xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=MKD+Bakery,Tegeta+Nyaishozi,Dar+es+Salaam,Tanzania&zoom=17"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MKD Bakery Location - Tegeta Nyaishozi"
                data-testid="google-map"
              />
            </div>
            <p className="text-sm text-bakery-text/60 dark:text-bakery-bg/60 mt-3 text-center">
              Right off the Tegeta Nyaishozi bus stop on Bagamoyo Road
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
